import React from 'react';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import HeroSection from './components/HeroSection';
import BenefitsSection from './components/BenefitsSection';
import ApplicationProcess from './components/ApplicationProcess';
import ContractorSpotlight from './components/ContractorSpotlight';
import ResourceCenter from './components/ResourceCenter';
import PortalAccess from './components/PortalAccess';
import TerritoryMap from './components/TerritoryMap';
import CTASection from './components/CTASection';
import Footer from '../../components/Footer';

const ContractorsSEOSection = () => (
  <section className="py-16 px-4 bg-gray-50 border-t border-gray-200">
    <div className="max-w-5xl mx-auto">
      <h2 className="text-3xl font-bold text-gray-900 mb-6">Grow Your Roofing Business with OpenbooQ</h2>
      <p className="text-lg text-gray-600 leading-relaxed mb-5">
        OpenbooQ's contractor network is designed for professional roofing companies that want a steady, qualified pipeline of homeowner leads without the overhead of traditional marketing. Our platform pre-qualifies every homeowner before connecting them with a contractor — meaning you spend less time on unqualified leads and more time doing what you do best: installing roofs.
      </p>
      <p className="text-lg text-gray-600 leading-relaxed mb-8">
        As an OpenbooQ partner contractor, you gain access to our proprietary project management platform, transparent pricing tools, and a growing base of homeowners across the country who are actively seeking roof replacement services. Our model eliminates the need for commissioned salespeople and high-pressure tactics — homeowners come to you already informed, already priced, and ready to move forward.
      </p>
      <div className="grid md:grid-cols-3 gap-6">
        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
          <h3 className="text-lg font-semibold text-gray-900 mb-3">Qualified Lead Flow</h3>
          <p className="text-gray-600 text-sm leading-relaxed">
            Every homeowner in our system has already received an accurate price and made a conscious decision to move forward. You're not chasing cold leads — you're connecting with homeowners who are ready to schedule their installation.
          </p>
        </div>
        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
          <h3 className="text-lg font-semibold text-gray-900 mb-3">Transparent Project Management</h3>
          <p className="text-gray-600 text-sm leading-relaxed">
            Our contractor portal gives you full visibility into your project pipeline, scheduling, material orders, and payment status. No more chasing homeowners for approvals or waiting on material deliveries — everything is managed through one centralized platform.
          </p>
        </div>
        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
          <h3 className="text-lg font-semibold text-gray-900 mb-3">Territory Exclusivity</h3>
          <p className="text-gray-600 text-sm leading-relaxed">
            OpenbooQ offers territory-based contractor partnerships, meaning you won't be competing with dozens of other contractors for the same leads in your market. We work with a limited number of vetted partners per service area to ensure quality and consistent work volume for each partner.
          </p>
        </div>
      </div>
    </div>
  </section>
);

const ForContractorsPage = () => {
  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Home",
        "item": "https://openbooq.com"
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": "For Contractors",
        "item": "https://openbooq.com/for-contractors"
      }
    ]
  };

  return (
    <>
      <Helmet>
        <title>For Contractors - Partner with OpenbooQ | Grow Your Roofing Business</title>
        <meta 
          name="description" 
          content="Partner with OpenbooQ to grow your roofing business. Streamline your sales, access exclusive leads, and offer transparent estimates to your clients." 
        />
        <meta name="keywords" content="roofing contractors, contractor jobs, roofing business, contractor network, roofing opportunities" />
        <meta property="og:title" content="For Contractors - Partner with OpenbooQ" />
        <meta property="og:description" content="Join 500+ contractors growing their business with OpenbooQ. Transparent pricing, steady work flow, and professional support." />
        <meta property="og:type" content="website" />
        <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
      </Helmet>

      <div className="min-h-screen bg-white">
        <Header />
        
        <main>
          <HeroSection />
          <BenefitsSection />
          <ApplicationProcess />
          <ContractorSpotlight />
          <ResourceCenter />
          <PortalAccess />
          <TerritoryMap />
          <ContractorsSEOSection />
          <CTASection />
        </main>

        <Footer />
      </div>
    </>
  );
};

export default ForContractorsPage;